import { Check, Repeat, Video, VideoOff } from 'lucide-react'
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/components/ui/overlays'
import { Tooltip } from '@/components/ui/overlays'
import type { RecordingMode } from '@/data/types'
import { cn } from '@/lib/utils'

const LABELS: Record<RecordingMode, string> = {
  off: 'Not recording',
  meeting: 'Recording this meeting',
  series: 'Recording the whole series',
}

/**
 * Recording state is carried by the icon, and the series case by the small
 * repeat mark on it — no text label, because the row already has a title, a
 * time, participants and a project to read. The words live in the tooltip,
 * which is where a state that is obvious at a glance belongs.
 */
export function RecordButton({
  mode,
  isRecurring,
  onChange,
  busy,
}: {
  mode: RecordingMode
  isRecurring: boolean
  onChange: (mode: RecordingMode) => void
  busy?: boolean
}) {
  const recording = mode !== 'off'

  return (
    <Menu>
      {/* The tooltip wraps a span rather than the trigger itself: Base UI's
          Tooltip.Trigger clones its child, and cloning the Menu.Trigger
          detaches it from the menu — the button renders but never opens. */}
      <Tooltip content={LABELS[mode]}>
        <span className="inline-flex">
          <MenuTrigger
            disabled={busy}
            aria-label={`${LABELS[mode]}. Change recording`}
            className={cn(
              'flex h-6 w-9 items-center justify-center gap-0.5 rounded-[var(--radius-sm)] outline-none',
              'transition-colors duration-100 disabled:opacity-40',
              'hover:bg-[var(--color-surface-hover)]',
              recording ? 'text-[var(--color-fg)]' : 'text-[var(--color-fg-subtle)]'
            )}
          >
            {recording ? <Video className="size-3.5" /> : <VideoOff className="size-3.5" />}
            {/* The slot is reserved whether or not the mark is shown, so the
                video icon sits in the same place on every row. Overlaying the
                mark on the icon's corner read as a rendering artifact at this
                size; beside it, it reads as a qualifier. */}
            <span className="w-2 shrink-0">
              {mode === 'series' ? (
                <Repeat className="size-2 text-[var(--color-fg-muted)]" aria-hidden />
              ) : null}
            </span>
          </MenuTrigger>
        </span>
      </Tooltip>

      <MenuContent>
        <MenuItem className="text-xs" onClick={() => onChange('meeting')}>
          <Video className="size-3.5" />
          <span className="flex-1">Record this meeting</span>
          {mode === 'meeting' ? <Check className="size-3.5" /> : null}
        </MenuItem>

        {isRecurring ? (
          <MenuItem className="text-xs" onClick={() => onChange('series')}>
            <Repeat className="size-3.5" />
            <span className="flex-1">Record entire series</span>
            {mode === 'series' ? <Check className="size-3.5" /> : null}
          </MenuItem>
        ) : null}

        <MenuSeparator />

        <MenuItem className="text-xs" onClick={() => onChange('off')}>
          <VideoOff className="size-3.5" />
          <span className="flex-1">Don't record</span>
          {mode === 'off' ? <Check className="size-3.5" /> : null}
        </MenuItem>
      </MenuContent>
    </Menu>
  )
}
